// Native Web Push: VAPID push notifications (suscripción en segundo plano)
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { FirestoreService } from './firestore.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { firstValueFrom } from 'rxjs';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly http = inject(HttpClient);
  private readonly firestoreService = inject(FirestoreService);
  private readonly authService = inject(AuthService);
  private readonly logger = inject(LoggerService);

  private vapidPublicKey: string | null = null;

  private async getVapidPublicKey(): Promise<string | null> {
    if (this.vapidPublicKey) return this.vapidPublicKey;
    try {
      const res = await firstValueFrom(
        this.http.get<{ publicKey: string }>(`${environment.apiUrl}/notifications/vapid-public-key`)
      );
      // Validación: si el backend devuelve algo que no es una clave válida
      // (p. ej. `{}`), abortamos antes de intentar `subscribe`, que fallaría
      // con un error críptico.
      if (!res || typeof res.publicKey !== 'string' || res.publicKey.trim().length < 20) {
        this.logger.error('Clave pública VAPID inválida recibida del backend', res);
        return null;
      }
      this.vapidPublicKey = res.publicKey;
      return this.vapidPublicKey;
    } catch (err) {
      this.logger.error('No se pudo obtener la clave pública VAPID', err);
      return null;
    }
  }

  /**
   * Devuelve la suscripcion Web Push vigente. Si la clave VAPID del servidor cambio (rotacion), la
   * suscripcion anterior del navegador ya no sirve: se cancela y se crea una nueva con la clave actual.
   */
  private async getOrRefreshSubscription(
    reg: ServiceWorkerRegistration,
    vapidKey: string
  ): Promise<PushSubscription> {
    const wanted = urlBase64ToUint8Array(vapidKey);
    let subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      const current = subscription.options?.applicationServerKey;
      const same =
        !!current &&
        current.byteLength === wanted.byteLength &&
        new Uint8Array(current).every((b, i) => b === wanted[i]);
      if (!same) {
        await subscription.unsubscribe();
        subscription = null;
      }
    }
    return (
      subscription ??
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: wanted }))
    );
  }

  async requestPermissionAndGetToken(_registration?: ServiceWorkerRegistration): Promise<string | null> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      this.logger.warn('Web Push no está soportado en este navegador');
      return null;
    }

    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return null;

    const vapidKey = await this.getVapidPublicKey();
    if (!vapidKey) return null;

    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await this.getOrRefreshSubscription(reg, vapidKey);

      // Enviar la suscripción Web Push al backend
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/notifications/subscribe`, {
          subscription: subscription.toJSON(),
          platform: 'web'
        })
      );

      return JSON.stringify(subscription.toJSON());
    } catch (error) {
      this.logger.error('Error al suscribir Web Push:', error);
      return null;
    }
  }

  /** Suscripción a mensajes cuando la app está en foreground */
  registerForegroundHandler(handler: (payload: any) => void): () => void {
    if (!('serviceWorker' in navigator)) {
      return () => {};
    }

    const listener = (event: MessageEvent) => {
      if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
        handler(event.data.payload);
      }
    };

    navigator.serviceWorker.addEventListener('message', listener);
    return () => navigator.serviceWorker.removeEventListener('message', listener);
  }

  async sendTestNotification(): Promise<void> {
    const user = await firstValueFrom(this.authService.currentUser$);
    if (!user) {
      throw new Error('User not logged in');
    }

    await firstValueFrom(
      this.http.post(`${environment.apiUrl}/notifications/test`, {})
    );
  }

  async enableNotifications(userId: string): Promise<boolean> {
    const token = await this.requestPermissionAndGetToken();
    if (token) {
      await this.firestoreService.saveUserNotificationPreference(userId, true);
      return true;
    }

    // El permiso del navegador pudo concederse aunque la suscripción fallara
    // (p. ej. el service worker aún estaba inicializando). Si ya está concedido,
    // marcamos la cuenta como activada y reintentamos registrar el token.
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      await this.ensureTokenSync(userId);
      const sub = await this.getCurrentSubscription();
      if (sub) {
        await this.firestoreService.saveUserNotificationPreference(userId, true);
        return true;
      }
    }

    return false;
  }

  /** Suscripción push ya existente en este dispositivo (si la hay). */
  private async getCurrentSubscription(): Promise<PushSubscription | null> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    try {
      const reg = await navigator.serviceWorker.ready;
      return await reg.pushManager.getSubscription();
    } catch {
      return null;
    }
  }

  async ensureTokenSync(userId: string): Promise<void> {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const reg = await navigator.serviceWorker.ready;
      const vapidKey = await this.getVapidPublicKey();
      const subscription = vapidKey
        ? await this.getOrRefreshSubscription(reg, vapidKey)
        : await reg.pushManager.getSubscription();
      if (subscription) {
        await firstValueFrom(
          this.http.post(`${environment.apiUrl}/notifications/subscribe`, {
            subscription: subscription.toJSON(),
            platform: 'web'
          })
        );
        await this.firestoreService.saveUserNotificationPreference(userId, true);
      }
    } catch (err) {
      this.logger.warn('[PushService] ensureTokenSync:', err);
    }
  }

  async disableNotifications(userId: string): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          await sub.unsubscribe();
          try {
            await firstValueFrom(
              this.http.post(`${environment.apiUrl}/notifications/unsubscribe`, { endpoint })
            );
          } catch {}
        }
      }
    } catch (e) {
      this.logger.warn('Error al desuscribir Web Push:', e);
    }
    await this.firestoreService.saveUserNotificationPreference(userId, false);
  }
}
